import {
  findFacilityById,
  findPartnerById,
  findProceduresBySlugs,
  findProfessionalById,
  findProfessionalRatingById,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  findSchedulesByProcedures,
} from '@/app/(main)/(pages)/busca/[slug]/_services/find-schedules-by-procedures.service';
import { mapToSearchPartnerData } from '@/app/(main)/(pages)/_services/search-api/map-to-search-partner-data.util';
import { SearchPartnerData, SearchType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import {
  SearchApiFacility,
  SearchApiPartner,
  SearchApiProfessional,
  SearchApiRating,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { slugify } from '@/utils/formatters/slugfy';
import { searchResultsPageSize } from '@/utils/consts/search-schedules.consts';
import { SearchSortType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-sort.type';
import {
  SearchFilterAgeGroupRangeMap,
  SearchFilterAgeGroupType,
} from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-age-group.type';
import { SearchFilterDayTimeType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-day-time.type';
import { SearchFilterProfessionalGenderType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-filter-professional-gender.type';
import { getLogger } from '@/logger/logger';

const logger = getLogger('search.schedules');

export interface GetSchedulesFromSearchApiParams {
  slugs: string[];
  city?: string;
  state?: string;
  latitude?: number;
  longitude?: number;
  distance?: number;
  orderBy?: SearchSortType;
  ageGroup?: SearchFilterAgeGroupType;
  dayTime?: SearchFilterDayTimeType;
  professionalGender?: SearchFilterProfessionalGenderType;
  minDate?: string;
  maxDate?: string;
  followUp?: boolean;
  onlyScheduled?: boolean;
  page?: number;
  searchType?: string;
}

export interface GetSchedulesFromSearchApiResult {
  schedules: SearchPartnerData[];
  hasMore: boolean;
}

function toSearchType(value?: string): SearchType | undefined {
  const searchType = Object.values(SearchType).find((candidate) => candidate === value);

  if (!searchType && value) {
    logger.warn('Unrecognized search type', { search: { event: 'unrecognized_search_type', search_type: value } });
  }

  return searchType;
}

export async function getSchedulesFromSearchApi(
  params: GetSchedulesFromSearchApiParams,
): Promise<GetSchedulesFromSearchApiResult> {
  if (params.slugs.length === 0) {
    return { schedules: [], hasMore: false };
  }

  const procedures = await findProceduresBySlugs(params.slugs);

  if (procedures.length === 0) {
    return { schedules: [], hasMore: false };
  }

  const procedureIds = procedures.map((procedure) => procedure.id);

  const citySlug = params.city
    ? slugify(params.state ? `${params.city}-${params.state}` : params.city)
    : undefined;

  const { groups, hasMore } = await findSchedulesByProcedures({
    procedureIds,
    city: citySlug,
    latitude: params.latitude,
    longitude: params.longitude,
    distance: params.distance,
    orderBy: params.orderBy,
    dayTime: params.dayTime,
    professionalGender: params.professionalGender,
    minDate: params.minDate,
    maxDate: params.maxDate,
    followUp: params.followUp,
    onlyScheduled: params.onlyScheduled,
    ...SearchFilterAgeGroupRangeMap[params.ageGroup ?? SearchFilterAgeGroupType.ALL],
    page: params.page,
    limit: searchResultsPageSize,
  });

  const professionalData = new Map<string, Promise<SearchApiProfessional | null>>();
  const partnerData = new Map<string, Promise<SearchApiPartner | null>>();
  const facilityData = new Map<string, Promise<SearchApiFacility | null>>();
  const ratingData = new Map<string, Promise<SearchApiRating | null>>();

  const getProfessional = (id: string) => {
    if (!professionalData.has(id)) {
      professionalData.set(id, findProfessionalById(id).catch(() => null));
    }
    return professionalData.get(id)!;
  };

  const getPartner = (id: string) => {
    if (!partnerData.has(id)) {
      partnerData.set(id, findPartnerById(id).catch(() => null));
    }
    return partnerData.get(id)!;
  };

  const getFacility = (id: string) => {
    if (!facilityData.has(id)) {
      facilityData.set(id, findFacilityById(id).catch(() => null));
    }
    return facilityData.get(id)!;
  };

  const getRating = (id: string) => {
    if (!ratingData.has(id)) {
      ratingData.set(id, findProfessionalRatingById(id).catch(() => null));
    }
    return ratingData.get(id)!;
  };

  const schedules = await Promise.all(
    groups.map(async (group) => {
      const [professional, partner, facility, rating] = await Promise.all([
        group.professionalId ? getProfessional(group.professionalId) : Promise.resolve(null),
        group.partnerId ? getPartner(group.partnerId) : Promise.resolve(null),
        group.partnerFacilityId ? getFacility(group.partnerFacilityId) : Promise.resolve(null),
        group.professionalId ? getRating(group.professionalId) : Promise.resolve(null),
      ]);

      return mapToSearchPartnerData({
        group,
        procedures,
        professional,
        partner,
        facility,
        rating,
        searchType: toSearchType(params.searchType),
      });
    }),
  );

  return { schedules, hasMore };
}
